import React, { useState, useEffect, useRef } from "react";
import QuoteGradingResults from "./RefiBuddyResults/QuoteGradingResults";
import RefinanceAnalysisResults from "./RefiBuddyResults/RefinanceAnalysisResults";
import RefinanceEstimateResults from "./RefiBuddyResults/RefinanceEstimateResults";
import NationalRatesSection from "./shared/NationalRatesSection";
import DateCallout from "./shared/DateCallout";
import ProgressSteps from "./shared/ProgressSteps";
import ActionButtons from "./shared/ActionButtons";
import FAQSection from "./shared/FAQSection";
import SpeakToProfessionalModal from "./SpeakToProfessionalModal";
import StartNewRequestModal from "./StartNewRequestModal";
import { QuoteRequest, RateAnalysis } from "../types/rate-analysis";
import { useAuth } from "../contexts/AuthContext";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface RefiBuddyResultsProps {
  quoteRequest: QuoteRequest | undefined;
  analysis: RateAnalysis | null;
  disableTracking?: boolean;
}

const RefiBuddyResults: React.FC<RefiBuddyResultsProps> = ({
  quoteRequest,
  analysis,
  disableTracking = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewRequestModalOpen, setIsNewRequestModalOpen] = useState(false);
  const [isUpdateRequestModalOpen, setIsUpdateRequestModalOpen] =
    useState(false);

  const { user } = useAuth();
  const hasLoggedView = useRef(false);

  // Track quote analysis viewed for refinance requests
  useEffect(() => {
    if (
      analysis &&
      quoteRequest?.id &&
      !hasLoggedView.current &&
      !disableTracking
    ) {
      const body: Record<string, string> = {};
      if (user && user.email) {
        body.viewed_by = user.email;
      }
      fetch(`${API_BASE_URL}/analysis/${quoteRequest.id}/viewed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      hasLoggedView.current = true;
    }
  }, [analysis, quoteRequest?.id, user, disableTracking]);

  const handleStartNewRequest = () => {
    // Clear the current request ID and navigate to the form
    localStorage.removeItem("quote_request_id");
    window.location.href = "/should-i-refinance";
  };

  const getScenario = (quoteRequest: QuoteRequest) => {
    const hasBrokerRate = !!quoteRequest.broker_rate;
    const hasCurrentMortgage = !!quoteRequest.current_mortgage;

    if (hasBrokerRate) {
      return "QUOTE_GRADING";
    }

    if (hasCurrentMortgage) {
      return "REFINANCE_ANALYSIS";
    }

    return "REFINANCE_ESTIMATE";
  };

  // If we have a request ID but no analysis yet, show the analyzing state
  if (!analysis && quoteRequest) {
    return (
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
          You're All Set!
        </h1>
        <p className="text-xl text-gray-600">
          We're monitoring your loan scenario and will email you when it could
          make sense to refinance.
        </p>

        <ProgressSteps />

        <ActionButtons
          onSpeakToExpert={() => setIsModalOpen(true)}
          onStartNewRequest={handleStartNewRequest}
          variant="secondary"
        />

        <FAQSection />

        <SpeakToProfessionalModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          quoteRequestId={quoteRequest?.id}
        />
        <SpeakToProfessionalModal
          isOpen={isUpdateRequestModalOpen}
          onClose={() => setIsUpdateRequestModalOpen(false)}
          isUpdateRequest={true}
          quoteRequestId={quoteRequest?.id}
        />
      </div>
    );
  }

  // If we have both request ID and analysis, show the appropriate results
  if (!quoteRequest) {
    return null;
  }

  const scenario = getScenario(quoteRequest);

  return (
    <>
      {scenario === "QUOTE_GRADING" && analysis && (
        <>
          <NationalRatesSection />
          <DateCallout
            quoteRequest={quoteRequest}
            onRequestUpdate={() => setIsUpdateRequestModalOpen(true)}
          />
          <QuoteGradingResults
            analysis={analysis}
            quoteRequest={quoteRequest}
          />
        </>
      )}

      {scenario === "REFINANCE_ANALYSIS" && analysis && (
        <RefinanceAnalysisResults
          analysis={analysis}
          quoteRequest={quoteRequest}
          onRequestUpdate={() => setIsUpdateRequestModalOpen(true)}
        />
      )}

      {scenario === "REFINANCE_ESTIMATE" && analysis && (
        <>
          <NationalRatesSection />
          <DateCallout
            quoteRequest={quoteRequest}
            onRequestUpdate={() => setIsUpdateRequestModalOpen(true)}
          />
          <RefinanceEstimateResults
            analysis={analysis}
            quoteRequest={quoteRequest}
          />
        </>
      )}

      <ActionButtons
        onSpeakToExpert={() => setIsModalOpen(true)}
        onStartNewRequest={handleStartNewRequest}
        variant="primary"
      />

      <SpeakToProfessionalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <SpeakToProfessionalModal
        isOpen={isUpdateRequestModalOpen}
        onClose={() => setIsUpdateRequestModalOpen(false)}
        isUpdateRequest={true}
      />
      <StartNewRequestModal
        isOpen={isNewRequestModalOpen}
        onClose={() => setIsNewRequestModalOpen(false)}
        onConfirm={handleStartNewRequest}
      />
    </>
  );
};

export default RefiBuddyResults;
